import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Email, Lock } from "@mui/icons-material";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useMutationByMethod } from "../services/hooks/useMutationByMethod";
import { GetUrl, PostUrl } from "../api/endpointConfig";
import { ToastService } from "../utils/toastService";
import { useLoader } from "../Loader/LoaderContext";
import { useGet } from "../services/hooks/useGet";
interface LoginFormProps {
  onChangeForgot: (statevalue: boolean) => void;
}
const ChangePassword: React.FC<LoginFormProps> = ({ onChangeForgot }) => {
  const [step, setStep] = useState<"email" | "current" | "reset">("email");
  const [email, setEmail] = useState("");
  const { showLoader, hideLoader } = useLoader();

  const { mutate: verifyEmail } = useMutationByMethod<any>(
    "post",
    `${PostUrl.auth}/verify-email`
  );
  const { mutate: verifyCurrent } = useMutationByMethod<any>(
    "post",
    `${PostUrl.auth}/verify-current-password`
  );
  const { mutate: updatePassword } = useMutationByMethod<any>(
    "post",
    `${PostUrl.postChangePassword}`
  );

  // const {
  //   data,
  //   refetch: getEmailVerify,
  // } = useGet<any>(
  //   email ? `${GetUrl.getEmailVerify}?email=${email}` : "",
  //   undefined,
  //   false
  // );

  // Step 1 → Verify Email
  const { data, refetch } = useGet<any>(
    email ? `${GetUrl.getEmailVerify}?email=${email}` : "",
    {},
    false
  );

  const handleEmailSubmit = async (values: { email: string }) => {
    setEmail(values.email); // update first
  };

  useEffect(() => {
    if (!email) return;

    const verifyEmail = async () => {
      showLoader();
      try {
        const { data } = await refetch();

        if (data?.message) {
          ToastService.success(data?.message);
          setStep("reset");
        } else {
          ToastService.error("Email not found!");
        }
      } catch (error: any) {
        ToastService.error(error?.message || "Verification failed");
      } finally {
        hideLoader();
      }
    };

    verifyEmail();
  }, [email]);

  // Step 2 → Verify Current Password
  const handleCurrentSubmit = (values: { currentPassword: string }) => {
    showLoader();
    verifyCurrent(
      { email, currentPassword: values.currentPassword },
      {
        onSuccess: () => {
          ToastService.success("Current password verified!");
          setStep("reset");
          hideLoader();
        },
        onError: (err: any) => {
          ToastService.error(err.data.detail || "Incorrect password");
          hideLoader();
        },
      }
    );
  };

  // Step 3 → Set New Password
  const handleResetSubmit = (values: {
    currentPassword: string;
    password: string;
    confirmPassword: string;
  }) => {
    showLoader();
    const obj = {
      email: email,
      old_password: values.currentPassword,
      new_password: values.password,
    };
    updatePassword(obj, {
      onSuccess: () => {
        ToastService.success("Password changed successfully!");
        hideLoader();
        onChangeForgot(false);
      },
      onError: (err: any) => {
        ToastService.error(err.data.detail || "Failed to update password");
        hideLoader();
      },
    });
  };

  // Validation Schemas
  const emailSchema = Yup.object().shape({
    email: Yup.string().email("Invalid email").required("Email is required"),
  });
  const currentSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Current password is required"),
  });
  const resetSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Current password is required"),
    password: Yup.string().min(6, "At least 6 characters").required("Required"),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref("password")], "Passwords must match")
      .required("Required"),
  });

  return (
    <Box width={"100%"}>
      <Typography variant="h6" mb={2}>
        Change Password
      </Typography>

      {/* Step 1 → Email */}
      {step === "email" && (
        <Formik
          initialValues={{ email: "" }}
          validationSchema={emailSchema}
          onSubmit={handleEmailSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <Box className="form-group">
                <Field
                  as={TextField}
                  name="email"
                  label="Email Address"
                  fullWidth
                  variant="outlined"
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Email />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                className="buttonSec"
                display="flex"
                justifyContent={"center"}
                gap={2}
              >
                <Button
                  type="button"
                  variant="contained"
                  color="info"
                  className="submit-button"
                  onClick={() => {
                    onChangeForgot(false);
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="submit-button"
                >
                  Verify Email
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {/* Step 2 → Current Password */}
      {step === "current" && (
        <Formik
          initialValues={{ currentPassword: "" }}
          validationSchema={currentSchema}
          onSubmit={handleCurrentSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <Box className="form-group">
                <Field
                  as={TextField}
                  name="currentPassword"
                  type="password"
                  label="Current Password"
                  fullWidth
                  variant="outlined"
                  error={
                    touched.currentPassword && Boolean(errors.currentPassword)
                  }
                  helperText={touched.currentPassword && errors.currentPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" variant="contained" sx={{ mt: 2 }}>
                  Verify
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      )}

      {/* Step 3 → New Password */}
      {step === "reset" && (
        <Formik
          initialValues={{
            currentPassword: "",
            password: "",
            confirmPassword: "",
          }}
          validationSchema={resetSchema}
          onSubmit={handleResetSubmit}
        >
          {({ errors, touched }) => (
            <Form>
              <Box className="form-group">
                <Field
                  as={TextField}
                  name="currentPassword"
                  type="password"
                  label="Current Password"
                  fullWidth
                  variant="outlined"
                  error={
                    touched.currentPassword && Boolean(errors.currentPassword)
                  }
                  helperText={touched.currentPassword && errors.currentPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box className="form-group">
                <Field
                  as={TextField}
                  name="password"
                  type="password"
                  label="New Password"
                  fullWidth
                  variant="outlined"
                  error={touched.password && Boolean(errors.password)}
                  helperText={touched.password && errors.password}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box className="form-group">
                <Field
                  as={TextField}
                  name="confirmPassword"
                  type="password"
                  label="Confirm New Password"
                  fullWidth
                  variant="outlined"
                  error={
                    touched.confirmPassword && Boolean(errors.confirmPassword)
                  }
                  helperText={touched.confirmPassword && errors.confirmPassword}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Box
                className="buttonSec"
                display="flex"
                justifyContent={"center"}
                gap={2}
              >
                <Button
                  type="button"
                  variant="contained"
                  color="info"
                  className="submit-button"
                  onClick={() => {
                    setStep("email");
                  }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  className="submit-button"
                >
                  Update
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      )}
    </Box>
  );
};

export default ChangePassword;
